/**
 * Layout presets for floor plan and dashboard layouts
 * Defines standard aspect ratios for resolution-independent layouts
 */

export interface LayoutPreset {
  id: string;
  name: string;
  ratioX: number;
  ratioY: number;
  aspectRatio: number;
  category: 'widescreen' | 'standard' | 'portrait' | 'square';
}

export const LAYOUT_PRESETS: LayoutPreset[] = [
  {
    id: 'widescreen-16-9',
    name: 'Widescreen (16:9)',
    ratioX: 16,
    ratioY: 9,
    aspectRatio: 16 / 9,
    category: 'widescreen',
  },
  {
    id: 'standard-4-3',
    name: 'Standard (4:3)',
    ratioX: 4,
    ratioY: 3,
    aspectRatio: 4 / 3,
    category: 'standard',
  },
  {
    id: 'photo-3-2',
    name: 'Photo (3:2)',
    ratioX: 3,
    ratioY: 2,
    aspectRatio: 3 / 2,
    category: 'standard',
  },
  {
    id: 'square-1-1',
    name: 'Square (1:1)',
    ratioX: 1,
    ratioY: 1,
    aspectRatio: 1,
    category: 'square',
  },
  {
    id: 'portrait-9-16',
    name: 'Portrait (9:16)',
    ratioX: 9,
    ratioY: 16,
    aspectRatio: 9 / 16,
    category: 'portrait',
  },
];

/**
 * Get a preset by ID
 */
export function getPreset(id: string): LayoutPreset | undefined {
  return LAYOUT_PRESETS.find((preset) => preset.id === id);
}

/**
 * Get all presets
 */
export function getAllPresets(): LayoutPreset[] {
  return LAYOUT_PRESETS;
}

/**
 * Get presets by category
 */
export function getPresetsByCategory(category: 'widescreen' | 'standard' | 'portrait' | 'square'): LayoutPreset[] {
  return LAYOUT_PRESETS.filter((preset) => preset.category === category);
}

/**
 * Get the default preset (Widescreen 16:9)
 */
export function getDefaultPreset(): LayoutPreset {
  return LAYOUT_PRESETS[0];
}

/**
 * Calculate dimensions from aspect ratio to fit within viewport
 * @param aspectRatio - Aspect ratio (width / height)
 * @param viewportWidth - Available viewport width
 * @param viewportHeight - Available viewport height
 * @param padding - Padding around canvas (in pixels)
 * @returns { width, height } in pixels
 */
export function calculateLayoutDimensions(
  aspectRatio: number,
  viewportWidth: number,
  viewportHeight: number,
  padding: number = 20,
): { width: number; height: number } {
  const availableWidth = Math.max(100, viewportWidth - padding * 2);
  const availableHeight = Math.max(56, viewportHeight - padding * 2);

  let width: number;
  let height: number;

  // Calculate based on viewport aspect ratio vs layout aspect ratio
  const viewportRatio = availableWidth / availableHeight;

  if (viewportRatio > aspectRatio) {
    // Viewport is wider - constrain by height
    height = availableHeight;
    width = height * aspectRatio;
  } else {
    // Viewport is taller - constrain by width
    width = availableWidth;
    height = width / aspectRatio;
  }

  // Ensure minimum dimensions
  return {
    width: Math.max(320, width),
    height: Math.max(180, height),
  };
}

/**
 * Legacy pixel preset ID to aspect ratio preset ID mapping
 * Used for auto-migration of old dashboards
 */
export const LEGACY_PRESET_MAPPING: Record<string, string> = {
  'desktop-1080p': 'widescreen-16-9',
  'desktop-768p': 'widescreen-16-9',
  'tablet-landscape': 'standard-4-3',
  'tablet-portrait': 'portrait-9-16',
  'mobile-iphone': 'widescreen-16-9',
};

/**
 * Legacy pixel preset dimensions for migration
 * Maps old preset IDs to their pixel dimensions
 */
export const LEGACY_PRESET_DIMENSIONS: Record<string, { width: number; height: number }> = {
  'desktop-1080p': { width: 1920, height: 1080 },
  'desktop-768p': { width: 1366, height: 768 },
  'tablet-landscape': { width: 1024, height: 768 },
  'tablet-portrait': { width: 768, height: 1024 },
  'mobile-iphone': { width: 375, height: 667 },
};
